/*
=========================================================
* RingFoods How It Works Section
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import StepContent from "@mui/material/StepContent";

// @mui icons
import LinkIcon from "@mui/icons-material/Link";
import SettingsIcon from "@mui/icons-material/Settings";
import RelaxIcon from "@mui/icons-material/SelfImprovement";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { grey100 } from "pages/RingFoodsHomepage/theme";

function HowItWorks() {
  const steps = [
    {
      label: "Connect Your Systems",
      icon: LinkIcon,
      title: "Link Google Calendar & Phone",
      description:
        "Connect your Google Calendar for real-time availability and forward your restaurant phone number to RingFoods AI. No technical expertise required.",
      details: [
        "One-click Google Calendar integration",
        "Phone number forwarding setup",
        "Twilio secure connection",
        "Takes less than 5 minutes",
      ],
    },
    {
      label: "Configure Your Restaurant",
      icon: SettingsIcon,
      title: "Upload Menu & Set Preferences",
      description:
        "Upload your menu (PDF or image) and set your restaurant preferences. Our AI automatically extracts all the information it needs.",
      details: [
        "Menu OCR in 9+ languages",
        "Business hours & holiday overrides",
        "Table capacity settings",
        "Custom greeting messages",
      ],
    },
    {
      label: "Go Back to Running Your Restaurant",
      icon: RelaxIcon,
      title: "Go Back to Running Your Restaurant",
      description:
        "From this moment on, every call is handled -- the angry callers, the language barriers, the 2am inquiries. You focus on your guests, your kitchen, and your life.",
      details: [
        "Angry callers handled with patience and empathy",
        "Every language auto-detected and served natively",
        "After-hours calls answered without waking you",
        "Live dashboard so you never lose visibility",
      ],
    },
  ];

  return (
    <MKBox
      component="section"
      py={{ xs: 8, md: 12 }}
      id="how-it-works"
      sx={{ backgroundColor: grey100 }}
    >
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 6, md: 8 } }}>
          <Grid item xs={12} lg={8} mx="auto" textAlign="center">
            <MKTypography
              variant="h2"
              fontWeight="bold"
              color="primary"
              sx={{ mb: 2, fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
            >
              30 Minutes to Peace of Mind
            </MKTypography>
            <MKTypography variant="body1" color="text" sx={{ fontSize: { xs: "1rem", md: "1.125rem" }, mb: 2 }}>
              From setup to your first AI-handled call in under 30 minutes.
            </MKTypography>
            <MKTypography variant="body2" color="text" sx={{ opacity: 0.7 }}>
              No technical expertise required. Our team provides white-glove onboarding support.
            </MKTypography>
          </Grid>
        </Grid>

        {/* Steps for Mobile */}
        <MKBox sx={{ display: { xs: "block", md: "none" } }}>
          <Stepper
            orientation="vertical"
            activeStep={-1}
            sx={{
              background: "transparent",
              backgroundColor: "transparent",
              backgroundImage: "none",
              boxShadow: "none",
              borderRadius: 0,
              p: 0,
              "& .MuiStepConnector-line": {
                borderColor: "rgba(26, 26, 26, 0.2)",
              },
            }}
          >
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Step key={index} expanded sx={{ backgroundColor: "transparent", mb: 3 }}>
                  <StepLabel
                    StepIconComponent={() => (
                      <MKBox
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          backgroundColor: "#1A1A1A",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                        }}
                      >
                        <Icon />
                      </MKBox>
                    )}
                  >
                    <MKTypography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: "1.15rem", md: "1.5rem" } }}>
                      {step.title}
                    </MKTypography>
                  </StepLabel>
                  <StepContent
                    sx={{
                      borderLeft: "2px solid rgba(26, 26, 26, 0.15)",
                      ml: "21px",
                    }}
                  >
                    <MKBox
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "white",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                        mb: 3,
                      }}
                    >
                      <MKTypography variant="body1" color="text" sx={{ mb: 2, fontSize: { xs: "1rem", md: "1.125rem" } }}>
                        {step.description}
                      </MKTypography>
                      {step.details.map((detail, detailIndex) => (
                        <MKBox
                          key={detailIndex}
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <CheckCircleIcon sx={{ color: "#22C55E", mr: 1, fontSize: 16 }} />
                          <MKTypography variant="body2" color="text">
                            {detail}
                          </MKTypography>
                        </MKBox>
                      ))}
                    </MKBox>
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>
        </MKBox>

        {/* Steps for Desktop */}
        <MKBox sx={{ display: { xs: "none", md: "block" } }}>
          <Grid container spacing={4}>
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                      {/* Step Number */}
                      <MKBox
                        sx={{
                          position: "relative",
                          mb: 3,
                        }}
                      >
                        <MKBox
                          sx={{
                            width: { xs: 70, md: 80 },
                            height: { xs: 70, md: 80 },
                            borderRadius: "50%",
                            backgroundColor: "#F0F0F0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                            mb: 2,
                            color: "#1A1A1A",
                          }}
                        >
                          <Icon sx={{ fontSize: { xs: 30, md: 35 } }} />
                        </MKBox>
                        <MKBox
                          sx={{
                            position: "absolute",
                            top: -10,
                            right: "calc(50% - 50px)",
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            backgroundColor: "#1A1A1A",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                          }}
                        >
                          {index + 1}
                        </MKBox>
                      </MKBox>

                      {/* Content */}
                      <MKTypography variant="h5" fontWeight="bold" sx={{ mb: 2, fontSize: { xs: "1.25rem", md: "1.5rem" } }}>
                        {step.title}
                      </MKTypography>
                      <MKTypography variant="body1" color="text" sx={{ mb: 3, fontSize: { xs: "1rem", md: "1.125rem" } }}>
                        {step.description}
                      </MKTypography>

                      {/* Details */}
                      <MKBox textAlign="left">
                        {step.details.map((detail, detailIndex) => (
                          <MKBox
                            key={detailIndex}
                            sx={{ display: "flex", alignItems: "center", mb: 1 }}
                          >
                            <CheckCircleIcon sx={{ color: "success.main", mr: 1, fontSize: 16 }} />
                            <MKTypography variant="body2" color="text">
                              {detail}
                            </MKTypography>
                          </MKBox>
                        ))}
                      </MKBox>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </MKBox>

      </Container>
    </MKBox>
  );
}

export default HowItWorks;
